import { useCallback, useEffect, useRef, useState } from "react";

const isElectron = () => {
  if (typeof window === "undefined") return false;
  return navigator.userAgent.toLowerCase().includes("electron");
};

const getClipboardText = async () => {
  if (isElectron()) {
    try {
      // eslint-disable-next-line global-require
      const { clipboard } = window.require("electron");
      return clipboard.readText();
    } catch {
      // sin acceso a electron, seguimos con la API web
    }
  }
  try {
    return await navigator.clipboard.readText();
  } catch {
    return "";
  }
};

const NON_TEXT_INPUT_TYPES = new Set([
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "range",
  "color",
  "file",
  "image",
]);

const findEditableTarget = (node) => {
  let el = node;
  while (el && el !== document.body) {
    if (el.tagName === "TEXTAREA" && !el.disabled && !el.readOnly) return el;
    if (
      el.tagName === "INPUT" &&
      !el.disabled &&
      !el.readOnly &&
      !NON_TEXT_INPUT_TYPES.has((el.getAttribute("type") || "text").toLowerCase())
    ) {
      return el;
    }
    if (el.isContentEditable) return el;
    el = el.parentElement;
  }
  return null;
};

const hasTextSelected = (target) => {
  if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
    return (target.selectionEnd ?? 0) !== (target.selectionStart ?? 0);
  }
  if (target.isContentEditable) {
    const sel = window.getSelection();
    return !!sel && sel.toString().length > 0;
  }
  return false;
};

const dispatchInput = (target) => target.dispatchEvent(new Event("input", { bubbles: true }));

// Electron no muestra un menú nativo de Cortar/Copiar/Pegar al hacer click
// derecho (a diferencia de un navegador). Este componente lo resuelve desde
// React para que funcione igual en TODOS los inputs/textareas/contentEditable
// de la app -en Electron y en el navegador-, sin tocar cada componente.
export default function PasteContextMenu() {
  const [menu, setMenu] = useState(null); // { x, y, target, hasSelection }
  const menuRef = useRef(null);

  const close = useCallback(() => setMenu(null), []);

  useEffect(() => {
    const onContextMenu = (e) => {
      const target = findEditableTarget(e.target);
      if (!target) return;
      e.preventDefault();
      target.focus();
      setMenu({ x: e.clientX, y: e.clientY, target, hasSelection: hasTextSelected(target) });
    };
    document.addEventListener("contextmenu", onContextMenu);
    return () => document.removeEventListener("contextmenu", onContextMenu);
  }, []);

  useEffect(() => {
    if (!menu) return undefined;
    const onMouseDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) close();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };
    const onScroll = () => close();
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [menu, close]);

  const handleCut = () => {
    const target = menu?.target;
    close();
    if (!target) return;
    target.focus();
    document.execCommand && document.execCommand("cut");
  };

  const handleCopy = () => {
    const target = menu?.target;
    close();
    if (!target) return;
    target.focus();
    document.execCommand && document.execCommand("copy");
  };

  const handlePaste = async () => {
    const target = menu?.target;
    close();
    if (!target) return;
    target.focus();

    const text = await getClipboardText();
    if (!text) return;

    const inserted = document.execCommand && document.execCommand("insertText", false, text);
    if (inserted) return;

    const isTextarea = target.tagName === "TEXTAREA";
    const proto = isTextarea ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
    const nativeSetter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? start;
    const newValue = target.value.slice(0, start) + text + target.value.slice(end);

    if (nativeSetter) {
      nativeSetter.call(target, newValue);
    } else {
      target.value = newValue;
    }
    dispatchInput(target);
    const caret = start + text.length;
    target.setSelectionRange?.(caret, caret);
  };

  const handleSelectAll = () => {
    const target = menu?.target;
    close();
    if (!target) return;
    target.focus();
    if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
      target.select();
    } else if (target.isContentEditable) {
      const range = document.createRange();
      range.selectNodeContents(target);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  if (!menu) return null;

  const items = menu.hasSelection
    ? [
        { key: "cut", label: "Cortar", onClick: handleCut },
        { key: "copy", label: "Copiar", onClick: handleCopy },
        { key: "paste", label: "Pegar", onClick: handlePaste },
        { key: "sep", separator: true },
        { key: "selectAll", label: "Seleccionar todo", onClick: handleSelectAll },
      ]
    : [
        { key: "paste", label: "Pegar", onClick: handlePaste },
        { key: "selectAll", label: "Seleccionar todo", onClick: handleSelectAll },
      ];

  return (
    <div
      ref={menuRef}
      onMouseDown={(e) => e.preventDefault()}
      style={{
        position: "fixed",
        top: menu.y,
        left: menu.x,
        zIndex: 20000,
        minWidth: 150,
        background: "#fff",
        border: "1px solid #d9d9d9",
        borderRadius: 6,
        boxShadow: "0 2px 10px rgba(0,0,0,0.18)",
        padding: 4,
        fontFamily: "inherit",
      }}
    >
      {items.map((item) =>
        item.separator ? (
          <div key={item.key} style={{ height: 1, background: "#eee", margin: "4px 0" }} />
        ) : (
          <div
            key={item.key}
            role="button"
            tabIndex={0}
            onClick={item.onClick}
            onKeyDown={(e) => e.key === "Enter" && item.onClick()}
            style={{
              padding: "6px 14px",
              fontSize: 14,
              borderRadius: 4,
              cursor: "pointer",
              userSelect: "none",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f0f0f0";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            {item.label}
          </div>
        )
      )}
    </div>
  );
}
